import { ISelectValue } from "~app/common/models/selectValueModel";
import { IArbetsorderDetail } from "~app/features/planera/model/IArbetsorderDetail";
import { IAtgard } from "~app/features/planera/model/IAtgard";
import { IUtbytesberakning } from "~app/features/planera/model/IUtbytesberakning";
import { ArbetsorderUtbyteBehaviour } from "../../behaviours/ArbetsorderUtbyteBehaviour";
import { AtgardUtbyteBehaviour } from "../../behaviours/AtgardUtbyteBehaviour";

export class ArbetsorderDirektutbyteViewModel {
    utbytesberakningar: IUtbytesberakning[];
    isAvverkning: boolean;
    hasGrotuttagProduktionsplanerat: boolean;

    private utbyteBehaviour: IUtbyteBehaviour;
    private isArbetsorder = false;
    private produktgrupper: ISelectValue[];
    private readonly timmerkategori = "Sågtimmer";

    private constructor(
        private readonly ref: IArbetsorderDetail | IAtgard,
        init: (instance: ArbetsorderDirektutbyteViewModel) => void) {
        this.utbytesberakningar = this.ref.utbytesberakningar;
        init(this);
    }

    get hasArbetsorderJusteradeUtbyten(): boolean {
        return this.isArbetsorder && this.utbytesberakningar?.some(p => p.justeradKvantitet > 0);
    }

    get summaUtbyten(): string {
        return `${this.getSummaUtbyten()}`;
    }

    get summaUtbytenJusterat(): string {
        return `${this.getSummaUtbytenJusterat()}`;
    }

    get summaTimmerandelar(): string {
        const summaUtbyten = this.getSummaUtbyten();
        if (summaUtbyten === 0) {
            return "0";
        }

        const utbyten = this.utbytesberakningar
            .filter(utbyte => utbyte.handelssortimentKategori === this.timmerkategori);
        const kvantiteter = utbyten.map(p => p.volymM3fub);
        const summa = kvantiteter.reduce((sum, num) => sum + num, 0);
        return `${Math.round((summa / summaUtbyten) * 100)}%`;
    }

    get summaTimmerandelarJusterat(): string {
        const summaUtbyten = this.getSummaUtbytenJusterat();
        if (summaUtbyten === 0) {
            return "0";
        }

        const utbyten = this.utbytesberakningar
            .filter(utbyte => utbyte.handelssortimentKategori === this.timmerkategori);
        const kvantiteter = utbyten.map(p => p.justeradKvantitet || p.volymM3fub);
        const summa = kvantiteter.reduce((sum, num) => sum + num, 0);
        return `${Math.round((summa / summaUtbyten) * 100)}%`;
    }

    get direktutbyteGrot(): number {
        return this.utbyteBehaviour.direktUtbyteGrot;
    }

    get valbaraProduktgrupper(): ISelectValue[] {
        if (!this.ref || !this.isArbetsorder) {
            return [];
        }

        const selected = this.ref.utbytesberakningar
            .filter((f) => f.produktgruppId !== null)
            .map((d) => d.produktgruppId);
        return this.produktgrupper.filter((f) => selected.indexOf(f.id) === -1);
    }

    static createAsAtgard(ref: IAtgard, arbetsorder: IArbetsorderDetail): ArbetsorderDirektutbyteViewModel {
        return new ArbetsorderDirektutbyteViewModel(ref, (instance) => {
            instance.isAvverkning = arbetsorder && arbetsorder.isAvverkning;
            instance.hasGrotuttagProduktionsplanerat = ref.grotuttagProduktionsplanerat;
            instance.utbyteBehaviour = new AtgardUtbyteBehaviour(ref);
        });
    }

    static createAsArbetsorder(ref: IArbetsorderDetail, produktgrupper: ISelectValue[]): ArbetsorderDirektutbyteViewModel {
        return new ArbetsorderDirektutbyteViewModel(ref, (instance) => {
            instance.produktgrupper = produktgrupper;
            instance.isArbetsorder = true;
            instance.isAvverkning = ref && ref.isAvverkning;
            instance.hasGrotuttagProduktionsplanerat = instance.isAvverkning &&
                ref.atgarder.some(x => x.grotuttagProduktionsplanerat);
            instance.utbyteBehaviour = new ArbetsorderUtbyteBehaviour(ref);
        });
    }

    reRender(): void {
        this.utbytesberakningar = undefined;
        setTimeout(() => {
            this.utbytesberakningar = this.ref.utbytesberakningar;
        }, 0);
    }

    private getSummaUtbyten(): number {
        if (!this.ref || !this.utbytesberakningar) {
            return 0;
        }

        const kvantiteter = this.utbytesberakningar.map(p => p.volymM3fub);
        return kvantiteter.reduce((sum, num) => sum + num, 0);
    }

    private getSummaUtbytenJusterat(): number {
        if (!this.ref || !this.utbytesberakningar) {
            return 0;
        }

        const kvantiteter = this.utbytesberakningar.map(p => p.justeradKvantitet || p.volymM3fub);
        return kvantiteter.reduce((sum, num) => sum + num, 0);
    }
}

interface IUtbyteBehaviour {
    direktUtbyteGrot: number;
}
